import { ProvenanceBadge } from "@ishvaram/ui/Jyotish";
import type {
  AccountJyotishPeriod,
  AccountJyotishSnapshot,
  AccountJyotishTransit,
  AccountRelationshipHome,
} from "./account-api";
import { formatAccountDate } from "./account-format";

type TodayGuidance = NonNullable<AccountRelationshipHome["today_guidance"]>;

export function AccountTodayJyotish({
  home,
  snapshot,
}: {
  home?: AccountRelationshipHome | null;
  snapshot?: AccountJyotishSnapshot | null;
}) {
  const guidance = home?.today_guidance;
  if (!guidance && !snapshot) return null;

  const isHindi = (guidance?.language || snapshot?.language) === "hi";
  const moonRashi =
    guidance?.moon_rashi_display ||
    (snapshot ? localizedName(snapshot.moon_rashi_en, snapshot.moon_rashi_hi, isHindi) : "");
  const asOf = guidance?.as_of || snapshot?.as_of;

  return (
    <section
      data-testid="account-jyotish-snapshot"
      aria-labelledby="today-jyotish-heading"
      className="mx-5 mt-5 overflow-hidden rounded-2xl border border-[var(--color-accent-border)] bg-[var(--color-surface-raised)] sm:mx-6"
    >
      <div className="grid md:grid-cols-[minmax(0,1.15fr)_minmax(18rem,0.85fr)]">
        <GuidancePanel guidance={guidance} isHindi={isHindi} moonRashi={moonRashi} asOf={asOf} />
        <ChartSignalsPanel guidance={guidance} snapshot={snapshot} isHindi={isHindi} />
      </div>
    </section>
  );
}

function GuidancePanel({
  guidance,
  isHindi,
  moonRashi,
  asOf,
}: {
  guidance?: TodayGuidance | null;
  isHindi: boolean;
  moonRashi: string;
  asOf?: string | null;
}) {
  return (
    <div className="p-5 sm:p-6">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-accent)]">
          {isHindi ? "Aaj aapki kundali mein" : "Today in your kundali"}
        </p>
        <GuidanceProvenance hasFocus={Boolean(guidance?.personal_focus)} isHindi={isHindi} />
      </div>
      <h3
        id="today-jyotish-heading"
        className="mt-3 max-w-xl font-serif text-2xl leading-tight text-[var(--color-text-main)]"
      >
        {guidance?.personal_focus?.headline || guidanceHeading(moonRashi, isHindi)}
      </h3>
      <p className="mt-3 max-w-xl text-sm leading-6 text-[var(--color-text-sub)]">
        {guidanceCopy(guidance, isHindi)}
      </p>
      <GuidanceAction action={guidance?.personal_focus?.action} />
      <MoonBackground guidance={guidance} moonRashi={moonRashi} isHindi={isHindi} />
      <GuidanceMetadata guidance={guidance} asOf={asOf} isHindi={isHindi} />
    </div>
  );
}

function GuidanceProvenance({ hasFocus, isHindi }: { hasFocus: boolean; isHindi: boolean }) {
  return hasFocus ? (
    <ProvenanceBadge
      kind="interpretation"
      label={isHindi ? "Engine se nirdeshit dhyan" : "Engine-guided focus"}
    />
  ) : (
    <ProvenanceBadge
      kind="pending"
      label={isHindi ? "Margdarshan refresh ho raha hai" : "Guidance refreshing"}
    />
  );
}

function GuidanceAction({ action }: { action?: string | null }) {
  return action ? (
    <p className="mt-4 rounded-xl border border-[var(--color-accent-border)] bg-[var(--color-surface-warm)] px-4 py-3 text-sm font-medium leading-6 text-[var(--color-text-main)]">
      {action}
    </p>
  ) : null;
}

function MoonBackground({
  guidance,
  moonRashi,
  isHindi,
}: {
  guidance?: TodayGuidance | null;
  moonRashi: string;
  isHindi: boolean;
}) {
  if (!guidance?.personal_focus || !guidance.rashifal) return null;
  return (
    <div className="mt-4 border-t border-[var(--color-border-light)] pt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-faint)]">
        {isHindi ? `${moonRashi} rashi ka background` : `${moonRashi} Moon background`}
      </p>
      <p className="mt-2 text-sm leading-6 text-[var(--color-text-sub)]">{guidance.rashifal}</p>
    </div>
  );
}

function GuidanceMetadata({
  guidance,
  asOf,
  isHindi,
}: {
  guidance?: TodayGuidance | null;
  asOf?: string | null;
  isHindi: boolean;
}) {
  if (!asOf) return null;
  return (
    <p className="mt-4 text-xs text-[var(--color-text-faint)]">
      {formatAccountDate(asOf)} · {guidanceSource(guidance, isHindi)}
    </p>
  );
}

function guidanceCopy(guidance: TodayGuidance | null | undefined, isHindi: boolean): string {
  return guidance?.personal_focus?.body || guidance?.rashifal || fallbackGuidance(isHindi);
}

function guidanceSource(guidance: TodayGuidance | null | undefined, isHindi: boolean): string {
  if (guidance?.personal_focus?.source_label) return guidance.personal_focus.source_label;
  if (guidance) {
    return isHindi
      ? "Aapki saved kundali aur aaj ke panchang par aadharit"
      : "Based on your saved chart and today’s panchang";
  }
  return isHindi
    ? "Aapki saved kundali ka engine snapshot"
    : "Engine snapshot from your saved chart";
}

function ChartSignalsPanel({
  guidance,
  snapshot,
  isHindi,
}: {
  guidance?: TodayGuidance | null;
  snapshot?: AccountJyotishSnapshot | null;
  isHindi: boolean;
}) {
  return (
    <div className="border-t border-[var(--color-border-light)] bg-[var(--color-surface-warm)] p-5 sm:p-6 md:border-l md:border-t-0">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-sub)]">
          {isHindi ? "Aaj ke graha sanket" : "Today’s chart signals"}
        </p>
        <ProvenanceBadge kind="engine" label="Engine-computed facts" />
      </div>

      {/* inert-by-design: these are computed facts, not controls or navigation. */}
      <dl className="mt-4 grid gap-4">
        {guidance?.panchang ? (
          <ChartFact
            label={isHindi ? "Aaj ka panchang" : "Today’s panchang"}
            value={`${guidance.panchang.paksha} ${guidance.panchang.tithi} · ${guidance.panchang.nakshatra}`}
          />
        ) : null}
        <DashaFact guidanceLord={guidance?.mahadasha_lord} snapshot={snapshot} isHindi={isHindi} />
        {snapshot?.transits.length ? (
          <TransitFacts transits={snapshot.transits} isHindi={isHindi} />
        ) : null}
      </dl>
      {snapshot ? (
        <p className="mt-4 text-xs text-[var(--color-text-faint)]">Lahiri sidereal</p>
      ) : null}
    </div>
  );
}

function TransitFacts({
  transits,
  isHindi,
}: {
  transits: AccountJyotishTransit[];
  isHindi: boolean;
}) {
  return (
    <div>
      <dt className="text-xs text-[var(--color-text-faint)]">
        {isHindi ? "Mukhya gochar" : "Key gochar"}
      </dt>
      <dd>
        <ul className="mt-2 space-y-2" aria-label="Current key transits">
          {transits.map((transit) => (
            <li key={transit.planet_id} className="text-sm leading-5 text-[var(--color-text-main)]">
              {transitLabel(transit, isHindi)}
            </li>
          ))}
        </ul>
      </dd>
    </div>
  );
}

function DashaFact({
  guidanceLord,
  snapshot,
  isHindi,
}: {
  guidanceLord?: string | null;
  snapshot?: AccountJyotishSnapshot | null;
  isHindi: boolean;
}) {
  const periods = snapshot
    ? [periodName(snapshot.mahadasha, isHindi), periodName(snapshot.antardasha, isHindi)].filter(
        Boolean
      )
    : [];
  const value = periods.length ? periods.join(" › ") : guidanceLord;
  return value ? (
    <ChartFact label={isHindi ? "Chal rahi dasha" : "Current dasha"} value={value} />
  ) : null;
}

function ChartFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-[var(--color-text-faint)]">{label}</dt>
      <dd className="mt-1 font-serif text-lg text-[var(--color-text-main)]">{value}</dd>
    </div>
  );
}

function guidanceHeading(moonRashi: string, isHindi: boolean): string {
  if (!moonRashi) return isHindi ? "Aaj aapke saved chart mein" : "Your personal guidance today";
  return isHindi ? `${moonRashi} Chandra rashi ke liye` : `For your ${moonRashi} Moon`;
}

function fallbackGuidance(isHindi: boolean): string {
  return isHindi
    ? "Aaj ka interpretation refresh ho raha hai. Aapka saved chart abhi bhi ready hai."
    : "Today’s interpretation is refreshing. Your saved chart is still ready.";
}

function periodName(period: AccountJyotishPeriod | null, isHindi: boolean): string {
  if (!period) return "";
  return localizedName(period.name_en, period.name_hi, isHindi);
}

function localizedName(english: string, hindi: string, isHindi: boolean): string {
  return isHindi ? hindi || english : english || hindi;
}

function transitLabel(transit: AccountJyotishTransit, isHindi: boolean): string {
  const planet = localizedName(transit.name_en, transit.name_hi, isHindi);
  const rashi = localizedName(transit.rashi_en, transit.rashi_hi, isHindi);
  const house = isHindi
    ? `Chandra se ${transit.house_from_moon} bhaav`
    : `${ordinal(transit.house_from_moon)} from Moon`;
  const retrograde = transit.is_retrograde ? (isHindi ? " · vakri" : " · retrograde") : "";
  return `${planet} · ${rashi} · ${house}${retrograde}`;
}

function ordinal(value: number): string {
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${value}th`;
  if (value % 10 === 1) return `${value}st`;
  if (value % 10 === 2) return `${value}nd`;
  if (value % 10 === 3) return `${value}rd`;
  return `${value}th`;
}
